import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';
import OphthalmologyIndicationsSection from './OphthalmologyIndicationsSection';
import {
  buildOphthalmologyIndicationsBody,
  emptyOphthalmologyIndicationSelections,
  indicationWorkflowLabelsFromSelections,
  ophthalmologyEmbeddedClassification,
} from './ophthalmologyIndicationsConstants';
import type {
  ClinicalActType,
  EvolutionAttachedPrintable,
  EvolutionIndicationItem,
} from './evolutionWorkflowTypes';

export interface EvolutionIndicationsFinishPayload {
  labels: string[];
  /**
   * Indicações com a identidade do procedimento (categoria e natureza do ato).
   * É o que permite o desfecho automático decidir o funil de CRM.
   */
  items: EvolutionIndicationItem[];
  /**
   * Seleções da grade da especialidade (chave do item + lateralidade). Antes eram
   * descartadas e só sobrava o texto impresso; guardamos para auditoria.
   */
  gridSelections?: Record<string, { od: boolean; oe: boolean; ao: boolean }>;
  /** Texto agrupado para «Imprimir todos» quando fizer sentido (ex.: oftalmologia). */
  printable?: EvolutionAttachedPrintable;
}

/** Extrai a natureza do ato clínico da linha devolvida pela busca de procedimentos. */
function actTypeFromProcedureRow(row: any): ClinicalActType | null {
  const cat = row?.category;
  const raw = (cat && typeof cat === 'object' ? cat.clinical_act_type : null)
    ?? row?.clinical_act_type
    ?? null;
  const value = String(raw || '').trim().toLowerCase();
  return ['consultation', 'exam', 'procedure', 'surgery'].includes(value)
    ? (value as ClinicalActType)
    : null;
}

function categoryFromProcedureRow(row: any): { id: string | null; name: string | null } {
  const cat = row?.category;
  if (cat && typeof cat === 'object') {
    return { id: cat.id ?? null, name: cat.name ?? null };
  }
  return { id: row?.category_id ?? null, name: row?.category_name ?? null };
}

/**
 * A grade da especialidade e o texto livre produzem só rótulos. O cadastro é
 * quem sabe a natureza do ato, então resolvemos os nomes no backend antes de
 * confirmar. Nome sem correspondência fica sem classificação — o desfecho pede
 * a escolha manual em vez de adivinhar.
 */
async function classifyIndicationLabels(labels: string[]): Promise<Map<string, Partial<EvolutionIndicationItem>>> {
  const out = new Map<string, Partial<EvolutionIndicationItem>>();
  const names = Array.from(new Set(labels.map((l) => l.trim()).filter(Boolean)));
  if (!names.length) return out;
  try {
    const { data } = await axios.post(getApiUrl('/clinic/procedures/classify'), { names });
    const list = Array.isArray(data?.data) ? data.data : [];
    for (const row of list) {
      const key = String(row?.query || '').trim().toLowerCase();
      if (!key) continue;
      out.set(key, {
        procedure_id: row?.procedure_id ?? null,
        category_id: row?.category_id ?? null,
        category_name: row?.category_name ?? null,
        act_type: (row?.clinical_act_type as ClinicalActType) ?? null,
      });
    }
  } catch {
    // Sem classificação o fluxo continua: o desfecho apenas pergunta ao médico.
  }
  return out;
}

/** "Catarata convencional (OD)" -> "Catarata convencional", para casar com o cadastro. */
function stripLateralitySuffix(label: string): string {
  return label.replace(/\s*\((?:OD|OE|AO)(?:\s*\/\s*(?:OD|OE|AO))*\)\s*$/i, '').trim();
}

interface EvolutionIndicationsDialogProps {
  open: boolean;
  onClose: () => void;
  /** Confirma rótulos e opcionalmente anexo de impressão. */
  onFinish: (payload: EvolutionIndicationsFinishPayload) => void;
}

function procedureSearchRowSecondary(row: any): string | undefined {
  if (row?.code) return String(row.code);
  const cat = row?.category;
  if (typeof cat === 'string' && cat.trim()) return cat.trim();
  if (cat && typeof cat === 'object') {
    const label = String(cat.name || cat.description || '').trim();
    if (label) return label;
  }
  const categoryName = String(row?.category_name || '').trim();
  return categoryName || undefined;
}

/**
 * Fluxo único para indicações (cirurgias/procedimentos) fora do assistente de impressos.
 * Oftalmologia: grade OD/OE/AO. Demais: busca no cadastro com fila antes de confirmar.
 */
const EvolutionIndicationsDialog: React.FC<EvolutionIndicationsDialogProps> = ({
  open,
  onClose,
  onFinish,
}) => {
  const [clinicMedicalSpecialty, setClinicMedicalSpecialty] = useState<string | null>(null);
  const [specialtyLoaded, setSpecialtyLoaded] = useState(false);

  const [q, setQ] = useState('');
  const [loadingRows, setLoadingRows] = useState(false);
  const [rows, setRows] = useState<any[]>([]);
  const [staged, setStaged] = useState<EvolutionIndicationItem[]>([]);
  const [committing, setCommitting] = useState(false);

  const [ophSel, setOphSel] = useState(() => emptyOphthalmologyIndicationSelections());
  const [ophOutros, setOphOutros] = useState('');
  const [ophObs, setOphObs] = useState('');

  const [freeOpen, setFreeOpen] = useState(false);
  const [freeBody, setFreeBody] = useState('');

  const isOphthalmology =
    specialtyLoaded && String(clinicMedicalSpecialty || '').toLowerCase() === 'oftalmologia';

  const resetAll = useCallback(() => {
    setQ('');
    setRows([]);
    setStaged([]);
    setOphSel(emptyOphthalmologyIndicationSelections());
    setOphOutros('');
    setOphObs('');
    setFreeBody('');
    setFreeOpen(false);
  }, []);

  useEffect(() => {
    if (!open) return;
    setSpecialtyLoaded(false);
    let cancelled = false;
    axios
      .get(getApiUrl('/clinic/settings'))
      .then((res) => {
        if (cancelled) return;
        const v = res.data?.data?.clinic_medical_specialty;
        setClinicMedicalSpecialty(typeof v === 'string' && v.trim() ? v.trim() : null);
      })
      .catch(() => {
        if (!cancelled) setClinicMedicalSpecialty(null);
      })
      .finally(() => {
        if (!cancelled) setSpecialtyLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open || isOphthalmology || !specialtyLoaded) return;
    const t = setTimeout(async () => {
      setLoadingRows(true);
      try {
        const { data } = await axios.get(getApiUrl('/clinic/budgets-search/procedures'), {
          params: { search: q || '' },
        });
        const list = data?.data ?? data;
        setRows(Array.isArray(list) ? list : []);
      } catch {
        setRows([]);
      } finally {
        setLoadingRows(false);
      }
    }, 280);
    return () => clearTimeout(t);
  }, [open, isOphthalmology, specialtyLoaded, q]);

  useEffect(() => {
    if (open) resetAll();
  }, [open, resetAll]);

  const filteredRows = useMemo(() => {
    if (!q.trim()) return rows;
    const t = q.trim().toLowerCase();
    return rows.filter(
      (r) =>
        String(r.name || '')
          .toLowerCase()
          .includes(t) ||
        String(r.code || '')
          .toLowerCase()
          .includes(t),
    );
  }, [rows, q]);

  const stagedNames = useMemo(() => staged.map((i) => i.name), [staged]);

  /** Item vindo da busca já nasce classificado — a linha traz a categoria via join. */
  const toggleStage = (row: any) => {
    const name = String(row?.name || row?.label || '').trim();
    if (!name) return;
    setStaged((prev) => {
      if (prev.some((i) => i.name === name)) return prev.filter((i) => i.name !== name);
      const category = categoryFromProcedureRow(row);
      return [
        ...prev,
        {
          name,
          procedure_id: row?.id ?? null,
          category_id: category.id,
          category_name: category.name,
          act_type: actTypeFromProcedureRow(row),
          source: 'catalog' as const,
        },
      ];
    });
  };

  const commitGenericStaging = () => {
    if (staged.length === 0) return;
    onFinish({ labels: stagedNames, items: staged });
    resetAll();
    onClose();
  };

  /**
   * Confirma resolvendo os rótulos contra o cadastro. A lateralidade é retirada
   * só para o casamento — o rótulo exibido e impresso continua com ela.
   */
  const commitWithClassification = async (
    labels: string[],
    source: 'specialty_grid' | 'free_text',
    printable: EvolutionAttachedPrintable,
    afterCommit?: () => void,
    gridSelections?: Record<string, { od: boolean; oe: boolean; ao: boolean }>,
  ) => {
    if (committing) return;
    setCommitting(true);
    try {
      const classification = await classifyIndicationLabels(labels.map(stripLateralitySuffix));
      const items: EvolutionIndicationItem[] = labels.map((label) => {
        const baseKey = stripLateralitySuffix(label).toLowerCase();
        const fromCatalog = classification.get(baseKey) || {};
        const embedded = source === 'specialty_grid' ? ophthalmologyEmbeddedClassification(baseKey) : null;
        return {
          name: label,
          source,
          ...embedded,
          ...fromCatalog,
          // Cadastro da clínica prevalece quando resolve; senão usa a grade embutida.
          act_type: fromCatalog.act_type ?? embedded?.act_type ?? null,
          category_name: fromCatalog.category_name ?? embedded?.category_name ?? null,
          procedure_id: fromCatalog.procedure_id ?? null,
          category_id: fromCatalog.category_id ?? null,
        };
      });
      onFinish({ labels, items, printable, gridSelections });
      afterCommit?.();
      resetAll();
      onClose();
    } finally {
      setCommitting(false);
    }
  };

  const commitOphthalmology = () => {
    const gridLabels = indicationWorkflowLabelsFromSelections(ophSel);
    const outrosExtra = ophOutros
      .split(/[,;\n]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    const bodyText = buildOphthalmologyIndicationsBody(ophSel, ophOutros, ophObs);
    if (gridLabels.length === 0 && !ophOutros.trim() && !ophObs.trim()) return;

    void commitWithClassification(
      [...gridLabels, ...outrosExtra],
      'specialty_grid',
      {
        clientKey: crypto.randomUUID(),
        category: 'indication',
        templateName: 'Indicações — Oftalmologia',
        bodyText,
      },
      undefined,
      // Só as linhas realmente marcadas — a grade inteira seria ruído no metadata.
      Object.fromEntries(Object.entries(ophSel).filter(([, v]) => v.od || v.oe || v.ao)),
    );
  };

  const commitFreeBody = () => {
    const lines = freeBody
      .split('\n')
      .map((s) =>
        s
          .replace(/^[\s\-•◦⋅]+\s*/, '')
          .trim(),
      )
      .filter(Boolean);
    if (!lines.length) return;
    const bodyText = ['INDICAÇÕES (texto livre)', '', ...lines.map((l) => `• ${l}`)].join('\n');
    void commitWithClassification(
      lines.slice(0, 40),
      'free_text',
      {
        clientKey: crypto.randomUUID(),
        category: 'indication',
        templateName: 'Indicações — texto livre',
        bodyText,
      },
      () => {
        setFreeBody('');
        setFreeOpen(false);
      },
    );
  };

  return (
    <>
      <Dialog
        open={open && !freeOpen}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{ sx: { borderRadius: 3, maxHeight: '90vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', pb: 1 }}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0f172a' }}>
              Indicações — cirurgias e procedimentos
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748b', display: 'block', maxWidth: 520 }}>
              Selecione e confirme. Este fluxo fica à parte dos impressos (receituário, atestados, exames).
            </Typography>
          </Box>
          <IconButton onClick={onClose} aria-label="Fechar" size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 1, minHeight: 360 }}>
          {!specialtyLoaded ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={32} sx={{ color: '#3b82f6' }} />
            </Box>
          ) : isOphthalmology ? (
            <Box sx={{ pt: 0.5 }}>
              <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                Unidade configurada como <strong>Oftalmologia</strong>: use a grade (OD/OE/AO). Para buscar pelo
                cadastro geral, use <strong>Texto livre / cadastro</strong>.
              </Alert>
              <OphthalmologyIndicationsSection
                selections={ophSel}
                onChangeSelections={setOphSel}
                outrosText={ophOutros}
                onChangeOutros={setOphOutros}
                observacoes={ophObs}
                onChangeObservacoes={setOphObs}
                onCommit={commitOphthalmology}
                onOpenFreeText={() => setFreeOpen(true)}
                confirmButtonText="Confirmar indicações na evolução"
              />
            </Box>
          ) : (
            <Box sx={{ pt: 0.5 }}>
              <Typography variant="body2" sx={{ color: '#475569', mb: 1.5 }}>
                Busque no cadastro da clínica. Toque num procedimento para incluir ou retirar da seleção antes de confirmar.
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Buscar FACO, YAG, pterígio..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#94a3b8', fontSize: 22 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 1.5 }}
              />
              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, letterSpacing: 0.06 }}>
                Selecionados ({staged.length})
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, my: 1, minHeight: 36 }}>
                {staged.length === 0 ? (
                  <Typography variant="body2" sx={{ color: '#94a3b8', fontStyle: 'italic' }}>
                    Nenhum — pesquise e toque nos itens da lista.
                  </Typography>
                ) : (
                  staged.map((item) => (
                    <Chip
                      key={item.name}
                      label={item.name}
                      size="small"
                      onDelete={() => setStaged((p) => p.filter((x) => x.name !== item.name))}
                      sx={{ fontWeight: 600 }}
                    />
                  ))
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                <Button size="small" variant="text" onClick={() => setStaged([])} sx={{ textTransform: 'none' }}>
                  Limpar seleção
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setFreeOpen(true)}
                  sx={{ textTransform: 'none', borderColor: '#cbd5e1', color: '#475569' }}
                >
                  Texto livre
                </Button>
              </Box>
              <Divider sx={{ mb: 1 }} />
              <Box sx={{ minHeight: 280, maxHeight: 360, overflow: 'auto' }}>
                {loadingRows ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                    <CircularProgress size={28} sx={{ color: '#3b82f6' }} />
                  </Box>
                ) : (
                  <List dense disablePadding>
                    {filteredRows.length === 0 ? (
                      <Typography variant="body2" sx={{ color: '#94a3b8', py: 2 }}>
                        Nenhum procedimento encontrado.
                      </Typography>
                    ) : (
                      filteredRows.map((row: any, idx: number) => {
                        const name = String(row.name || row.label || '').trim();
                        if (!name) return null;
                        const selected = stagedNames.includes(name);
                        return (
                          <ListItemButton
                            key={row.id || `${name}-${idx}`}
                            onClick={() => toggleStage(row)}
                            selected={selected}
                            sx={{
                              borderRadius: 2,
                              mb: 0.5,
                              border: '1px solid',
                              borderColor: selected ? 'rgba(59, 130, 246,0.45)' : 'transparent',
                              bgcolor: selected ? 'rgba(59, 130, 246,0.06)' : undefined,
                              '&:hover': { bgcolor: 'rgba(59, 130, 246,0.08)' },
                            }}
                          >
                            <ListItemText
                              primary={name}
                              secondary={procedureSearchRowSecondary(row)}
                              primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem' }}
                            />
                            <Typography variant="caption" sx={{ fontWeight: 700, color: '#3b82f6', flexShrink: 0 }}>
                              {selected ? 'Remover' : 'Incluir'}
                            </Typography>
                          </ListItemButton>
                        );
                      })
                    )}
                  </List>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, gap: 1, flexWrap: 'wrap' }}>
          <Button onClick={onClose} sx={{ textTransform: 'none', color: '#64748b' }}>
            Cancelar
          </Button>
          {!isOphthalmology ? (
            <Button
              variant="contained"
              disabled={staged.length === 0}
              onClick={commitGenericStaging}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                bgcolor: '#3b82f6',
                '&:hover': { bgcolor: '#2563eb' },
                '&.Mui-disabled': { bgcolor: '#cbd5e1' },
              }}
            >
              Confirmar na evolução{staged.length ? ` (${staged.length})` : ''}
            </Button>
          ) : (
            /* O oftalmológico usa o botão dentro da grade */
            null
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={freeOpen && open}
        onClose={() => setFreeOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
            Indicações — texto livre ou cadastro
          </Typography>
          <IconButton size="small" onClick={() => setFreeOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 1 }}>
          <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
            Uma linha por indicação, ou cole uma lista curta (também gera entrada para imprimir junto aos demais
            impressos da evolução, se desejado).
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={10}
            value={freeBody}
            onChange={(e) => setFreeBody(e.target.value)}
            placeholder={`Ex.: YAG capsulotomia OD\nFACO AO\n…`}
          />
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 2 }}>
          <Button onClick={() => setFreeOpen(false)} sx={{ textTransform: 'none' }}>
            Voltar
          </Button>
          <Button
            variant="contained"
            disabled={!freeBody.trim() || committing}
            onClick={commitFreeBody}
            sx={{ textTransform: 'none', fontWeight: 700, bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' } }}
          >
            {committing ? 'Confirmando…' : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EvolutionIndicationsDialog;
